import NeptuneElement from '../../NeptuneElement.js';

class FormButton extends NeptuneElement {
    static FORM_BUTTON_TYPES = ['submit', 'reset', 'button'];

    static FORM_BUTTON_SIZES = [
        'large',
        'default',
        'small',
        'full-width'
    ];

    static NEPTUNE_TYPE_MAP = {
        submit: 'primary',
        reset: 'secondary',
        button: 'primary'
    };

    static DEFAULT_TYPE = 'button';
    static DEFAULT_SIZE = 'default';

    templateMarkup = `
        <neptune-button type="primary" size="default">
            <slot></slot>
        </neptune-button>
    `;

    componentCssUrl = '/src/components/core/form-button/form-button.css';

    #neptuneButtonElement = null;
    #handleClickBound = null;

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.#setEventListeners();
        this.processInitialAttributesAfterRender(FormButton.observedAttributes);
    }

    async disconnectedCallback() {
        this.#removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['type', 'size', 'icon-start', 'icon-end'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        if (oldValue === newValue) { return; }

        switch (name) {
            case 'type': {
                const typeValue = newValue ?? FormButton.DEFAULT_TYPE;
                this.validatePropertyValue(name, typeValue, FormButton.FORM_BUTTON_TYPES);
                this.#applyNeptuneType(typeValue);
                break;
            }
            case 'size': {
                const sizeValue = newValue ?? FormButton.DEFAULT_SIZE;
                this.validatePropertyValue(name, sizeValue, FormButton.FORM_BUTTON_SIZES);
                this.#neptuneButtonElement?.setAttribute('size', sizeValue);
                break;
            }
            case 'icon-start': {
                this.#forwardAttribute('icon-start', newValue);
                break;
            }
            case 'icon-end': {
                this.#forwardAttribute('icon-end', newValue);
                break;
            }
            default:
                break;
        }
    }

    #setEventListeners() {
        this.#neptuneButtonElement = this.shadowRoot?.querySelector('neptune-button');
        this.#handleClickBound = this.#handleClick.bind(this);
        this.#neptuneButtonElement?.addEventListener('click', this.#handleClickBound);
    }

    #removeEventListeners() {
        this.#neptuneButtonElement?.removeEventListener('click', this.#handleClickBound);
        this.#neptuneButtonElement = null;
        this.#handleClickBound = null;
    }

    #applyNeptuneType(formType) {
        const neptuneType = FormButton.NEPTUNE_TYPE_MAP[formType];
        if (neptuneType && this.#neptuneButtonElement) {
            this.#neptuneButtonElement.setAttribute('type', neptuneType);
        }
    }

    #forwardAttribute(name, value) {
        if (!this.#neptuneButtonElement) { return; }
        if (value === null || value === undefined) {
            this.#neptuneButtonElement.removeAttribute(name);
        } else {
            this.#neptuneButtonElement.setAttribute(name, value);
        }
    }

    #handleClick(event) {
        const type = this.getAttribute('type') ?? FormButton.DEFAULT_TYPE;
        const form = this.closest('form');

        switch (type) {
            case 'submit': {
                if (form) {
                    if (typeof form.requestSubmit === 'function') {
                        form.requestSubmit();
                    } else {
                        form.submit();
                    }
                }
                break;
            }
            case 'reset': {
                if (form) {
                    form.reset();
                }
                break;
            }
            case 'button':
            default:
                break;
        }
    }
}

customElements.define('neptune-form-button', FormButton);
export default FormButton;